import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { FC } from 'react'
import { DeleteIcon, PlusIcon } from './Icons'

export type JobRunParamEntry = {
  key: string
  value: string
}

type JobRunParamsSectionProps = {
  params: JobRunParamEntry[]
  onChange: (params: JobRunParamEntry[]) => void
  disabled?: boolean
}

/**
 * Editor for the methodology's `jobRunParams` map. The platform substitutes `${key}` placeholders
 * in a workflow task's command with the matching value, so keys are free-form by design.
 */
const JobRunParamsSection: FC<JobRunParamsSectionProps> = ({ params, onChange, disabled }) => {
  const duplicateKeys = new Set(
    params
      .map(p => p.key.trim())
      .filter(key => key.length > 0)
      .filter((key, index, all) => all.indexOf(key) !== index)
  )

  const updateEntry = (index: number, patch: Partial<JobRunParamEntry>) => {
    onChange(params.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)))
  }

  return (
    <div className="col-span-1 mt-4 border-t-2 pt-4">
      <div className="flex items-center space-x-2">
        <label className="font-normal">Job Run Parameters</label>
        <Button
          type="button"
          className="icon-button"
          disabled={disabled}
          onClick={() => onChange([...params, { key: '', value: '' }])}
          tooltip="Add Job Run Parameter"
          tooltipOptions={{ position: 'bottom' }}>
          <PlusIcon className="fill-white w-4 h-4" />
        </Button>
      </div>
      <small className="text-gray-500">
        {'Key/value pairs used to fill ${key} placeholders in GM-HPC workflow task commands.'}
      </small>

      {params.length === 0 ? (
        <div className="mt-2 text-gray-500">No job run parameters defined.</div>
      ) : (
        <div className="mt-2 grid grid-cols-12 gap-x-2 gap-y-2 items-start">
          {params.map((entry, index) => (
            <div key={index} className="col-span-12 grid grid-cols-12 gap-x-2 items-center">
              <div className="field-box col-span-4">
                <InputText
                  aria-label={`Job run parameter key ${index + 1}`}
                  value={entry.key}
                  disabled={disabled}
                  placeholder="Key (e.g. queue)"
                  onChange={e => updateEntry(index, { key: e.target.value })}
                />
              </div>
              <div className="field-box col-span-7">
                <InputText
                  aria-label={`Job run parameter value ${index + 1}`}
                  value={entry.value}
                  disabled={disabled}
                  placeholder="Value"
                  onChange={e => updateEntry(index, { value: e.target.value })}
                />
              </div>
              <div className="col-span-1">
                <Button
                  type="button"
                  className="icon-button"
                  disabled={disabled}
                  onClick={() => onChange(params.filter((_, i) => i !== index))}
                  tooltip="Remove Job Run Parameter"
                  tooltipOptions={{ position: 'bottom' }}>
                  <DeleteIcon className="fill-white w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {duplicateKeys.size > 0 && (
        <small className="error">
          Duplicate keys will be collapsed to the last value: {Array.from(duplicateKeys).join(', ')}
        </small>
      )}
    </div>
  )
}

export default JobRunParamsSection
